import React from 'react';
import { useNavigation } from '../context/NavigationContext';
import { legalConfig, isFieldPlaceholder } from '../../config/legalConfig';
import { Shield, Lock, Eye, Calendar, ArrowLeft } from 'lucide-react';

export const PrivacyPolicyPage: React.FC = () => {
  const { navigate } = useNavigation();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
          <span>Ana Sayfaya Dön</span>
        </button>

        {/* Header */}
        <div className="border-b border-slate-800 pb-8 mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 mb-4">
            <Lock className="w-3.5 h-3.5" />
            <span>Gizlilik ve Veri Güvenliği</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
            GİZLİLİK POLİTİKASI
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-6 text-xs text-slate-400">
            <span className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-emerald-400" />
              <span>Veri Sorumlusu: <strong className="text-slate-200">{legalConfig.companyName}</strong></span>
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-emerald-400" />
              <span>Son Güncelleme: <strong className="text-slate-200">{legalConfig.lastUpdated}</strong></span>
            </span>
            <span className="text-slate-500">Sürüm: {legalConfig.privacyVersion}</span>
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">
            {legalConfig.relationshipStatement} Adiosoft platformunu ziyaret eden, üye olan veya dijital çözümlerimizi kullanan tüm kullanıcılarımızın kişisel verilerinin korunması ve gizliliği temel önceliğimizdir.
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-slate max-w-none text-sm leading-relaxed space-y-8 text-slate-300">
          <section className="space-y-3">
            <h2 className="text-base font-bold text-white">1. İşlenen Kişisel Veri Kategorileri</h2>
            <p>
              Adiosoft platformu üzerinde yalnızca hizmetin ifası, güvenliğin sağlanması ve mevzuattan kaynaklanan yükümlülüklerin yerine getirilmesi amacıyla gerçekte toplanan veriler işlenir:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-slate-300">
              <li><strong className="text-white">Hesap ve Kimlik Bilgileri:</strong> Ad, soyad, e-posta adresi, kullanıcı şifresi (tek yönlü kriptografik hash olarak saklanır), şirket/unvan bilgisi.</li>
              <li><strong className="text-white">İletişim Bilgileri:</strong> Telefon numarası, fatura ve tebligat adresi, iletişim formu talepleri.</li>
              <li><strong className="text-white">Ödeme ve Sipariş Bilgileri:</strong> Satın alınan abonelik planı, sipariş numarası, ödeme tutarı, lisans başlangıç ve bitiş tarihleri, ödeme sağlayıcısından dönen işlem referans kodu. <em>(Kredi kartı numarası, son kullanma tarihi veya CVV kodları şirketimiz sunucularında asla tutulmaz; doğrudan lisanslı ödeme altyapısı tarafından işlenir.)</em></li>
              <li><strong className="text-white">Teknik ve Log Verileri:</strong> IP adresi, tarayıcı türü, oturum açma tarih/saat damgaları, HTTP istek kayıtları, hata logları.</li>
              <li><strong className="text-white">Web Sitesi ve İçerik Verileri:</strong> Kullanıcının web sitesi editörüne eklediği metinler, görseller, sektörel modül verileri ve alan adı (domain) eşleştirme kayıtları.</li>
              <li><strong className="text-white">AI Yerel Görünürlük Analiz Verileri:</strong> Kullanıcı tarafından test edilen işletme adı, lokasyon sorguları ve üretilen optimizasyon raporları.</li>
              <li><strong className="text-white">Altyapı ve Lisans Bilgileri:</strong> Harici alan adı bağlantı talepleri ve teknik altyapı kayıtları.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-white">2. Verilerin İşlenme Amaçları</h2>
            <p>Toplanan kişisel veriler aşağıdaki amaçlar doğrultusunda işlenmektedir:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
              <li>Kullanıcı hesabının oluşturulması, kimlik doğrulamasının yapılması ve oturum güvenliğinin sağlanması,</li>
              <li>Web sitesi oluşturma, düzenleme, yayınlama ve özel alan adı bağlantı hizmetlerinin sunulması,</li>
              <li>Sipariş ve faturalandırma süreçlerinin yönetilmesi, dijital lisans haklarının tanımlanması,</li>
              <li>AI Local Visibility analizlerinin ve optimizasyon önerilerinin üretilmesi,</li>
              <li>Lisans ve altyapı bağlantı taleplerinin değerlendirilmesi ve kurulumunun yürütülmesi,</li>
              <li>İletişim ve destek taleplerine yanıt verilmesi,</li>
              <li>Bilgi güvenliği, siber saldırı önleme ve yasal kayıt saklama yükümlülüklerinin yerine getirilmesi.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-white">3. Veri Güvenliği ve Altyapı Standartları</h2>
            <p>
              Tüm veriler, endüstri standardı 256-bit SSL şifreleme ile iletilir ve yüksek güvenlikli bulut altyapılarında izole veri tabanı mimarisi altında saklanır. Verilere erişim, yalnızca yetkili sistem yöneticileri ile sınırlandırılmıştır.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-white">4. Veri Aktarımı ve Alıcı Grupları</h2>
            <p>
              Kişisel verileriniz hiçbir surette üçüncü kişilere pazarlama veya reklam amacıyla satılmaz. Veriler yalnızca hizmetin zorunlu kıldığı ölçüde şu alıcı gruplarına aktarılabilir:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
              <li><strong className="text-slate-200">Güvenli Bulut ve Barındırma Sağlayıcıları:</strong> Platformun kesintisiz ve yüksek performansla çalışması amacıyla küresel bulut altyapı sağlayıcıları,</li>
              <li><strong className="text-slate-200">Lisanslı Ödeme Hizmeti Sağlayıcısı (Shopier):</strong> Güvenli ödeme tahsilatı ve yasal faturalama koordinasyonu için,</li>
              <li><strong className="text-slate-200">E-Posta ve Bildirim Servisleri:</strong> Şifre sıfırlama, sipariş onayı ve hesap bildirimlerinin iletilmesi için,</li>
              <li><strong className="text-slate-200">Yetkili Kamu Kurum ve Kuruluşları:</strong> Yasal mevzuatın emrettiği hallerde veya adli talepler kapsamında.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-white">5. Veri Saklama ve İmha Süreleri</h2>
            <p>
              Kullanıcı hesabı silinse dahi; Türk Ticaret Kanunu, Vergi Usul Kanunu ve Elektronik Ticaret mevzuatı gereğince mali belgeler, sipariş geçmişi ve sözleşme onay kayıtları kanunen zorunlu olan saklama süreleri (genel olarak 10 yıl) boyunca arşivlenir. Süresi dolan veriler güvenli yöntemlerle silinir, yok edilir veya anonim hale getirilir.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-white">6. Kullanıcı Hakları ve İletişim</h2>
            <p>
              Kullanıcılar; verilerinin işlenip işlenmediğini öğrenme, yanlış verilerin düzeltilmesini isteme ve mevzuatın tanıdığı diğer hakları kullanma yetkisine sahiptir. Taleplerinizi <strong className="text-white">{legalConfig.privacyEmail}</strong> adresine iletebilirsiniz.
            </p>
          </section>
        </div>

        {/* Company Box */}
        <div className="mt-12 p-6 rounded-2xl bg-slate-900 border border-slate-800 text-xs text-slate-400 space-y-2">
          <p className="font-semibold text-slate-200">Veri Sorumlusu İletişim Bilgileri:</p>
          <p>Şirket: <strong className="text-white">{legalConfig.companyName}</strong></p>
          <p>Adres: {legalConfig.registeredOffice}</p>
          <p>Gizlilik E-Postası: {legalConfig.privacyEmail}</p>
        </div>
      </div>
    </div>
  );
};
export default PrivacyPolicyPage;
